/**
 * SmartEvaluate — Consultant Recommendation Override UI.
 *
 * Renders the System vs Final dual-display block, the inline override editor
 * (no modal), the "Override Active" advisory badge, the Clear-override
 * action, and the collapsible audit-history viewer.
 *
 * Backend payload contract (already shipped):
 *   GET    /api/admin/assessments/{id}/recommendations/effective
 *   POST   /api/admin/assessments/{id}/recommendations/override
 *   DELETE /api/admin/assessments/{id}/recommendations/override
 *
 * This component never re-runs scoring / capability / recommendation
 * engines. It only writes the consultant override layer and reads back the
 * effective values + audit trail.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, RotateCcw, History, X, Check, ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import {
  applyRecommendationOverride,
  clearRecommendationOverride,
  getEffectiveRecommendations,
} from "../lib/adminApi";


// ---------------------------------------------------------------------------
// Shared hook — fetches effective recommendations once + exposes refresh().
// Mounted by the Recommendations tab; the panel components consume via props.
// ---------------------------------------------------------------------------
export function useEffectiveRecommendations(assessmentId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    try {
      const r = await getEffectiveRecommendations(assessmentId);
      setData(r);
    } catch (e) {
      // 400 on non-SmartEvaluate assessments is expected — silence.
      if (e?.response?.status !== 400) {
        console.error("Failed to load effective recommendations", e);
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { data, loading, refresh };
}


// ---------------------------------------------------------------------------
// Small visual primitives
// ---------------------------------------------------------------------------
export function OverrideActiveBadge({ testId = "override-badge", compact = false }) {
  return (
    <span
      data-testid={testId}
      className={
        "inline-flex items-center gap-1 rounded-sm font-semibold border " +
        (compact
          ? "px-1.5 py-0.5 text-[9px] bg-[#E0F2FE] border-[#0093CB]/40 text-[#025979]"
          : "px-2 py-0.5 text-[10px] bg-[#E0F2FE] border-[#0093CB]/40 text-[#025979]")
      }
      title="Consultant recommendation override applied"
    >
      <ShieldCheck className="w-3 h-3" strokeWidth={2.25} />
      Override Active
    </span>
  );
}


// Status badge for the redesigned panel header: shows whether the displayed
// Final Recommendation mirrors the system output or carries a consultant override.
export function StatusBadge({ hasOverride, testId = "status-badge", compact = false }) {
  const pad = compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  if (hasOverride) {
    return (
      <span
        data-testid={testId}
        className={"inline-flex items-center gap-1 rounded-sm font-bold border bg-[#E0F2FE] border-[#0093CB]/40 text-[#025979] " + pad}
        title="Consultant override applied"
      >
        <ShieldCheck className="w-3 h-3" strokeWidth={2.25} />
        Consultant Override Applied
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className={"inline-flex items-center gap-1 rounded-sm font-semibold border bg-[#F8FAFC] border-[#E5E7EB] text-[#6B7280] uppercase tracking-wider " + pad}
      title="Displaying the system-generated recommendation"
    >
      System Recommendation
    </span>
  );
}


function ScopeRow({ label, value, testId, accent = false }) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[9px] uppercase tracking-[0.08em] text-[#9CA3AF] font-semibold">
        {label}
      </p>
      <p
        className={
          "text-[13px] leading-snug font-bold " +
          (accent ? "text-[#025979]" : "text-[#111827]")
        }
        data-testid={testId}
      >
        {value || "—"}
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Inline editor
// ---------------------------------------------------------------------------
function OverrideEditor({
  open,
  onClose,
  initialSolution,
  initialDeployment,
  initialHosting,
  allowedSolutions,
  allowedDeployments,
  deploymentOptionsBySolution,
  showHosting,
  allowedHostingModels,
  systemHosting,
  minReasonLength,
  onSave,
  saving,
  testIdPrefix,
  hideDeployment = false,
  solutionLabel = "Final Solution",
  allowSystemOption = true,
  fallbackSolution = "",
}) {
  const [solution, setSolution] = useState(initialSolution || (allowSystemOption ? "" : fallbackSolution));
  const [solutionOther, setSolutionOther] = useState("");
  const [deployment, setDeployment] = useState(initialDeployment || "");
  const hostingBaseline = systemHosting || "Evaluate Best Fit";
  const [hosting, setHosting] = useState(initialHosting || hostingBaseline);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setSolution(initialSolution || (allowSystemOption ? "" : fallbackSolution));
      setDeployment(initialDeployment || "");
      setHosting(initialHosting || hostingBaseline);
      setSolutionOther("");
      setReason("");
      setTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSolution, initialDeployment, initialHosting]);

  // Deployment options bounded by the selected Final Solution. When no solution
  // is chosen (keep system) or solution is "Other", show every deployment.
  const availableDeployments = useMemo(() => {
    if (!solution || solution === "Other") return allowedDeployments || [];
    const map = deploymentOptionsBySolution || {};
    return map[solution] || allowedDeployments || [];
  }, [solution, deploymentOptionsBySolution, allowedDeployments]);

  // When the solution changes, reconcile deployment: auto-select if exactly one
  // valid value exists, otherwise drop an incompatible selection.
  const handleSolutionChange = (val) => {
    setSolution(val);
    if (val && val !== "Other") {
      const opts = (deploymentOptionsBySolution || {})[val] || (allowedDeployments || []);
      if (opts.length === 1) setDeployment(opts[0]);
      else if (!opts.includes(deployment)) setDeployment("");
    }
  };

  if (!open) return null;

  const isOther = solution === "Other";
  const reasonShort = reason.trim().length < minReasonLength;
  // Hosting counts as a change only when it differs from the system baseline.
  const hostingChanged = !!(showHosting && hosting && hosting !== hostingBaseline);
  const nothingSet = !solution && !deployment && !hostingChanged;
  const otherMissing = isOther && solutionOther.trim().length < 3;
  // Block invalid solution↔deployment combinations (mirrors backend validation).
  const invalidCombo = !!(solution && solution !== "Other" && deployment
    && !availableDeployments.includes(deployment));
  const disabled = saving || reasonShort || nothingSet || otherMissing || invalidCombo;

  const handleSave = () => {
    setTouched(true);
    if (disabled) return;
    onSave({
      solution: solution || undefined,
      solution_other: isOther ? solutionOther.trim() : undefined,
      deployment: deployment || undefined,
      hosting_model: hostingChanged ? hosting : undefined,
      reason: reason.trim(),
    });
  };

  return (
    <div
      data-testid={`${testIdPrefix}-editor`}
      className="mt-3 border border-[#0093CB]/40 bg-[#F0F9FF] rounded-sm p-3 space-y-3"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-[#025979] font-bold">
          Advisory Amendment
        </p>
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testIdPrefix}-editor-close`}
          className="text-[#6B7280] hover:text-[#111827] transition-colors"
          aria-label="Close override editor"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[9px] uppercase tracking-wider text-[#6B7280] font-semibold">
            {solutionLabel}
          </span>
          <select
            data-testid={`${testIdPrefix}-editor-solution`}
            value={solution}
            onChange={(e) => handleSolutionChange(e.target.value)}
            className="mt-0.5 w-full text-[12px] bg-white border border-[#CBD5E1] rounded-sm px-2 py-1 focus:outline-none focus:border-[#0093CB]"
          >
            {allowSystemOption && <option value="">— Keep system —</option>}
            {(allowedSolutions || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {isOther && (
            <input
              type="text"
              value={solutionOther}
              onChange={(e) => setSolutionOther(e.target.value)}
              data-testid={`${testIdPrefix}-editor-solution-other`}
              placeholder="Free-text solution label"
              className="mt-1 w-full text-[12px] bg-white border border-[#CBD5E1] rounded-sm px-2 py-1 focus:outline-none focus:border-[#0093CB]"
            />
          )}
          {touched && otherMissing && (
            <p className="mt-1 text-[10px] text-[#B91C1C]">
              Provide a free-text label when "Other" is selected.
            </p>
          )}
        </label>

        {!hideDeployment && (
        <label className="block">
          <span className="text-[9px] uppercase tracking-wider text-[#6B7280] font-semibold">
            Final Deployment
          </span>
          <select
            data-testid={`${testIdPrefix}-editor-deployment`}
            value={deployment}
            onChange={(e) => setDeployment(e.target.value)}
            className="mt-0.5 w-full text-[12px] bg-white border border-[#CBD5E1] rounded-sm px-2 py-1 focus:outline-none focus:border-[#0093CB]"
          >
            <option value="">— Keep system —</option>
            {(availableDeployments || []).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {touched && invalidCombo && (
            <p className="mt-1 text-[10px] text-[#B91C1C]" data-testid={`${testIdPrefix}-editor-combo-error`}>
              "{deployment}" is not valid for {solution}.
            </p>
          )}
        </label>
        )}
      </div>

      {showHosting && (
        <label className="block">
          <span className="text-[9px] uppercase tracking-wider text-[#6B7280] font-semibold">
            Hosting Recommendation
          </span>
          <select
            data-testid={`${testIdPrefix}-editor-hosting`}
            value={hosting}
            onChange={(e) => setHosting(e.target.value)}
            className="mt-0.5 w-full text-[12px] bg-white border border-[#CBD5E1] rounded-sm px-2 py-1 focus:outline-none focus:border-[#0093CB]"
          >
            {(allowedHostingModels || []).map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </label>
      )}

      <label className="block">
        <span className="text-[9px] uppercase tracking-wider text-[#6B7280] font-semibold">
          Override Reason
          <span className="ml-1 text-[#9CA3AF] font-normal normal-case tracking-normal">
            ({reason.trim().length} / min {minReasonLength})
          </span>
        </span>
        <textarea
          data-testid={`${testIdPrefix}-editor-reason`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Capture the consulting rationale — e.g. customer budget constraints, phase-1 simplification, infrastructure alignment."
          className="mt-0.5 w-full text-[12px] bg-white border border-[#CBD5E1] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#0093CB] resize-none"
        />
        {touched && reasonShort && (
          <p className="mt-1 text-[10px] text-[#B91C1C]" data-testid={`${testIdPrefix}-editor-reason-error`}>
            Reason is required and must be at least {minReasonLength} characters.
          </p>
        )}
        {touched && nothingSet && (
          <p className="mt-1 text-[10px] text-[#B91C1C]">
            Set at least one of Final Solution or Final Deployment.
          </p>
        )}
      </label>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testIdPrefix}-editor-cancel`}
          className="inline-flex items-center gap-1 px-2.5 py-1 border border-[#E5E7EB] rounded-sm text-[11px] font-semibold text-[#374151] bg-white hover:bg-[#F8FAFC] transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          aria-disabled={disabled}
          disabled={saving}
          data-testid={`${testIdPrefix}-editor-save`}
          className={
            "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-semibold text-white transition-colors " +
            (saving
              ? "bg-[#9CA3AF] cursor-wait"
              : (disabled
                  ? "bg-[#0093CB]/60 hover:bg-[#0093CB]/70"
                  : "bg-[#0093CB] hover:bg-[#025979]"))
          }
        >
          <Check className="w-3.5 h-3.5" strokeWidth={2.25} />
          {saving ? "Saving…" : "Save Override"}
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Clear-override confirm row (inline; no modal)
// ---------------------------------------------------------------------------
function ClearOverrideRow({ open, minReasonLength, saving, onClose, onConfirm, testIdPrefix }) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => { if (open) { setReason(""); setTouched(false); } }, [open]);

  if (!open) return null;
  const reasonShort = reason.trim().length < minReasonLength;

  return (
    <div
      data-testid={`${testIdPrefix}-clear`}
      className="mt-3 border border-[#FCD34D] bg-[#FFFBEB] rounded-sm p-3 space-y-2"
    >
      <p className="text-[10px] uppercase tracking-wider text-[#92400E] font-bold">
        Reset to System Recommendation
      </p>
      <textarea
        data-testid={`${testIdPrefix}-clear-reason`}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Capture why the recommendation is being reset to system values (≥ 20 characters)…"
        className="w-full text-[12px] bg-white border border-[#FCD34D] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#F59E0B] resize-none"
      />
      {touched && reasonShort && (
        <p className="text-[10px] text-[#B91C1C]">
          Reason is required and must be at least {minReasonLength} characters.
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          data-testid={`${testIdPrefix}-clear-cancel`}
          className="inline-flex items-center gap-1 px-2.5 py-1 border border-[#E5E7EB] rounded-sm text-[11px] font-semibold text-[#374151] bg-white hover:bg-[#F8FAFC]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { setTouched(true); if (!reasonShort) onConfirm(reason.trim()); }}
          disabled={saving || reasonShort}
          data-testid={`${testIdPrefix}-clear-confirm`}
          className={
            "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-semibold text-white transition-colors " +
            ((saving || reasonShort) ? "bg-[#9CA3AF] cursor-not-allowed" : "bg-[#F59E0B] hover:bg-[#B45309]")
          }
        >
          <RotateCcw className="w-3.5 h-3.5" strokeWidth={2.25} />
          {saving ? "Resetting…" : "Confirm Reset"}
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Public — single override panel (used for global + per-site)
// ---------------------------------------------------------------------------
export function RecommendationOverridePanel({
  assessmentId,
  scope,                  // "global" | "site"
  siteCode,               // required if scope === "site"
  systemSolution,
  systemDeployment,
  systemHosting,          // programme-level system-derived hosting (global only)
  effectiveSolution,
  effectiveDeployment,
  effectiveHosting,       // programme-level effective hosting (global only)
  hasOverride,
  overrideEntry,          // current override block (for reason display)
  canOverride,            // from API summary
  cannotOverrideReason,   // optional message
  allowedSolutions,
  allowedDeployments,
  deploymentOptionsBySolution,
  allowedHostingModels,
  minReasonLength = 20,
  onChanged,              // called after a successful save / clear
  compact = false,        // tighter padding for the per-site card variant
  testIdPrefix,
  hideDeployment = false, // Cloud Migration: approach only, no deployment row
  hideHosting = false,    // Cloud Migration: no hosting model row
  solutionRowLabel = "Solution",
  solutionEditorLabel = "Final Solution",
  panelTitle,
  allowSystemOption = true,
  fallbackSolution = "",
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [origOpen, setOrigOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const showHosting = scope === "global" && !hideHosting;
  const idPrefix = testIdPrefix
    || (scope === "site" ? `override-site-${siteCode}` : "override-global");

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      await applyRecommendationOverride(assessmentId, {
        scope, site_code: scope === "site" ? siteCode : undefined, ...payload,
      });
      if (onChanged) await onChanged();
      setEditorOpen(false);
      toast.success("Consultant override applied successfully.");
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || "Failed to save override.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (reason) => {
    setSaving(true);
    try {
      await clearRecommendationOverride(assessmentId, {
        scope, site_code: scope === "site" ? siteCode : undefined, reason,
      });
      if (onChanged) await onChanged();
      setClearOpen(false);
      toast.success("Recommendation reset to system values.");
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || "Failed to reset recommendation.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`${idPrefix}-panel`}
      className={
        "border rounded-sm bg-white " +
        (hasOverride ? "border-[#0093CB]/50" : "border-[#E5E7EB]") + " " +
        (compact ? "p-3 space-y-2.5" : "p-4 space-y-3")
      }
    >
      {/* Header — title + status badge */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">
          {panelTitle || (scope === "global" ? "Programme Recommendation" : "Site Recommendation")}
        </p>
        <StatusBadge hasOverride={hasOverride} testId={`${idPrefix}-status-badge`} compact={compact} />
      </div>

      {/* Final recommendation (the single source of truth shown by default) */}
      <div
        data-testid={`${idPrefix}-final-card`}
        className={
          "rounded-sm p-3 space-y-2.5 " +
          (hasOverride
            ? "bg-gradient-to-br from-[#F0F9FF] to-white border border-[#0093CB]/40"
            : "bg-[#F8FAFC] border border-[#E5E7EB]")
        }
      >
        <p className="text-[10px] uppercase tracking-[0.1em] text-[#025979] font-bold pb-1.5 border-b border-[#E5E7EB]">
          Final Recommendation
        </p>
        <ScopeRow label={solutionRowLabel} value={effectiveSolution} accent={hasOverride}
                  testId={`${idPrefix}-final-solution`} />
        {!hideDeployment && (
          <ScopeRow label="Deployment" value={effectiveDeployment} accent={hasOverride}
                    testId={`${idPrefix}-final-deployment`} />
        )}
        {showHosting && (
          <ScopeRow label="Hosting Recommendation" value={effectiveHosting} accent={hasOverride}
                    testId={`${idPrefix}-final-hosting`} />
        )}
        {hasOverride && overrideEntry?.reason && (
          <p
            data-testid={`${idPrefix}-reason`}
            className="text-[11px] italic text-[#475569] pt-1.5 mt-1 border-t border-[#0093CB]/20 leading-snug"
          >
            "{overrideEntry.reason}"
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!canOverride}
          onClick={() => { setEditorOpen((o) => !o); setClearOpen(false); }}
          data-testid={`${idPrefix}-apply-btn`}
          className={
            "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-semibold transition-colors " +
            (canOverride
              ? "border border-[#0093CB]/40 text-[#025979] bg-white hover:bg-[#E0F2FE]"
              : "border border-[#E5E7EB] text-[#9CA3AF] bg-[#F8FAFC] cursor-not-allowed")
          }
          title={canOverride ? "Edit recommendation"
                             : (cannotOverrideReason
                                || "Create a new version to apply recommendation amendments.")}
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
          Edit Recommendation
        </button>

        {hasOverride && canOverride && (
          <button
            type="button"
            onClick={() => { setClearOpen(true); setEditorOpen(false); }}
            data-testid={`${idPrefix}-clear-btn`}
            className="inline-flex items-center gap-1 px-2.5 py-1 border border-[#FCD34D] rounded-sm text-[11px] font-semibold text-[#92400E] bg-white hover:bg-[#FFFBEB]"
          >
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={2} />
            {scope === "global" ? "Reset to System Recommendation" : "Reset"}
          </button>
        )}

        {!canOverride && (
          <p
            data-testid={`${idPrefix}-disabled-reason`}
            className="text-[10.5px] italic text-[#92400E]"
          >
            {cannotOverrideReason
              || "Create a new version to apply recommendation amendments."}
          </p>
        )}
      </div>

      <OverrideEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initialSolution={overrideEntry?.solution || ""}
        initialDeployment={overrideEntry?.deployment || ""}
        initialHosting={overrideEntry?.hosting_model || ""}
        allowedSolutions={allowedSolutions}
        allowedDeployments={allowedDeployments}
        deploymentOptionsBySolution={deploymentOptionsBySolution}
        showHosting={showHosting}
        allowedHostingModels={allowedHostingModels}
        systemHosting={systemHosting}
        minReasonLength={minReasonLength}
        onSave={handleSave}
        saving={saving}
        testIdPrefix={idPrefix}
        hideDeployment={hideDeployment}
        solutionLabel={solutionEditorLabel}
        allowSystemOption={allowSystemOption}
        fallbackSolution={fallbackSolution}
      />

      <ClearOverrideRow
        open={clearOpen}
        minReasonLength={minReasonLength}
        saving={saving}
        onClose={() => setClearOpen(false)}
        onConfirm={handleClear}
        testIdPrefix={idPrefix}
      />

      {/* Collapsible — original (system) recommendation for auditability */}
      <div className="pt-1">
        <button
          type="button"
          onClick={() => setOrigOpen((o) => !o)}
          data-testid={`${idPrefix}-view-original-toggle`}
          className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#6B7280] hover:text-[#025979] transition-colors"
        >
          {origOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          View Original Recommendation
        </button>
        {origOpen && (
          <div
            data-testid={`${idPrefix}-system-card`}
            className="mt-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3 space-y-2.5"
          >
            <p className="text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF] font-bold pb-1.5 border-b border-[#E5E7EB]">
              System Recommendation
            </p>
            <ScopeRow label={solutionRowLabel} value={systemSolution}
                      testId={`${idPrefix}-system-solution`} />
            {!hideDeployment && (
              <ScopeRow label="Deployment" value={systemDeployment}
                        testId={`${idPrefix}-system-deployment`} />
            )}
            {showHosting && (
              <ScopeRow label="Hosting Recommendation" value={systemHosting || "Not Decided"}
                        testId={`${idPrefix}-system-hosting`} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Public — Override history viewer (collapsible, compact)
// ---------------------------------------------------------------------------
function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}


export function OverrideHistoryViewer({ history }) {
  const [open, setOpen] = useState(false);
  const items = useMemo(
    () => Array.isArray(history) ? history.slice().reverse() : [],
    [history],
  );
  if (items.length === 0) return null;

  return (
    <section data-testid="override-history" className="mt-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="override-history-toggle"
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#025979] hover:text-[#0093CB]"
      >
        <History className="w-3.5 h-3.5" strokeWidth={2} />
        {open ? "Hide" : "View"} Override History
        <span className="text-[10px] text-[#9CA3AF] font-normal">
          ({items.length} entr{items.length === 1 ? "y" : "ies"})
        </span>
      </button>

      {open && (
        <ol
          data-testid="override-history-list"
          className="mt-2 border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB] bg-white"
        >
          {items.map((h, i) => {
            const isClear = (h.source || "") === "consultant_override_clear";
            return (
              <li
                key={`history-${i}`}
                data-testid={`override-history-row-${i}`}
                className="p-3 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-2"
              >
                <div className="space-y-0.5">
                  <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">
                    {fmtTs(h.overridden_at)}
                  </p>
                  <p className="text-[11px] text-[#111827] font-semibold">
                    {h.scope === "global" ? "Programme" : `Site · ${h.site_code || "—"}`}
                  </p>
                  <p className="text-[10px] text-[#6B7280]">
                    {h.overridden_by || "—"}
                  </p>
                  {isClear && (
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-[#FFFBEB] border border-[#FCD34D] text-[#92400E] rounded-sm font-bold">
                      Reverted
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#111827] space-y-1">
                  <p>
                    <span className="text-[#6B7280]">Solution:</span>{" "}
                    <span className="font-semibold">{h.previous_solution || "—"}</span>
                    {" → "}
                    <span className="font-semibold text-[#025979]">{h.new_solution || "—"}</span>
                  </p>
                  <p>
                    <span className="text-[#6B7280]">Deployment:</span>{" "}
                    <span className="font-semibold">{h.previous_deployment || "—"}</span>
                    {" → "}
                    <span className="font-semibold text-[#025979]">{h.new_deployment || "—"}</span>
                  </p>
                  {h.override_reason && (
                    <p className="text-[10.5px] italic text-[#475569] pt-0.5">
                      "{h.override_reason}"
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
